import { useEffect } from "react";
import ChatMessage from "../../Components/ChatMessage/ChatMessage";
import { useTicketStore } from "../../Stores/Ticket";

interface TypingIndicatorProps {
  isLoading?: boolean;
  scrollToBottom: () => void;
}

const TypingIndicator = ({
  isLoading,
  scrollToBottom,
}: TypingIndicatorProps) => {
  const ticketStore = useTicketStore();
  const isPassToAgent =
    ticketStore.currentBotAction?.action?.type === "passtoagent";

  // For passtoagent, only show typing when the server explicitly sends it
  // (typingSource === "server"). Local typing indicators and isLoading are ignored
  // because the server controls response timing to simulate human behavior.
  const showTypingIndicator = isPassToAgent
    ? ticketStore.typingUser && ticketStore.typingSource === "server"
    : ticketStore.typingUser || isLoading;
  const botWrittingText = ticketStore.botWrittingText;

  useEffect(() => {
    scrollToBottom();
  }, [showTypingIndicator, botWrittingText]);

  // The indicator has to carry the SAME author header as the message that will
  // replace it, or the avatar pops in and appears to flicker as the widget
  // moves between indicator and answer. A server typing event names a real
  // teammate; the local placeholder the bot flows set (`{id: "bot"}`) carries
  // no `firstName`, so as a TEXT row it resolved to no author at all — it
  // renders as a BOT row instead and picks up the same bot mark the streaming
  // answer and the settled message use.
  const typingUserIsNamed = !!ticketStore.typingUser?.firstName;

  // A server typing event that carries the agent identity (same fields the
  // settled reply ships) renders as the AGENT's own author group — "Kai
  // Resolve" with its workspace avatar (bot-mark fallback) — so the indicator
  // and the reply that replaces it share one identity and nothing flickers.
  const typingAgent = ticketStore.typingAgent;

  if (showTypingIndicator && !botWrittingText) {
    if (typingAgent?.agentName) {
      return (
        <ChatMessage
          key="typing"
          firstOfGroup={true}
          lastOfGroup={true}
          chatMessage={{
            data: {
              content: "Typing…",
              agentName: typingAgent.agentName,
              ...(typingAgent.agentAvatarUrl
                ? { agentAvatarUrl: typingAgent.agentAvatarUrl }
                : {}),
            },
            user: undefined,
            session: undefined,
            type: "BOT",
          }}
          forceTyping={true}
          showTime={false}
        />
      );
    }
    return (
      <ChatMessage
        key="typing"
        firstOfGroup={true}
        lastOfGroup={true}
        chatMessage={{
          data: {
            content: "Typing…",
          },
          user: typingUserIsNamed ? ticketStore.typingUser : undefined,
          type: typingUserIsNamed ? "TEXT" : "BOT",
        }}
        forceTyping={true}
        showTime={false}
      />
    );
  }

  return <></>;
};

export default TypingIndicator;
